import { TradeInAemNavStep, TradeInAemSearchType } from "../../../utils/constants";
import { TradeInCsl } from "../../TradeInCsl";
import type {
    TradeInAemEHubPayload,
    TradeInAemExtraVehicleDetailsPdfBaseType,
    TradeInAemFetchQuoteFunctionReturnType,
    TradeInAemFetchVehicleResultsReturnType,
    TradeInAemVehicleExtraInfoType,
    TradeInAemVehicleNameType,
    TradeInAemVehicleType,
    TradeInAemVehicleValueType,
} from "../../../utils/types";
import {
    enablePdf,
    fetchFindVehicle,
    fetchVehicleValue,
    getParsedVehicleDetails,
    getTradeInAemPdfUrl,
    handleQuoteFetch,
    handleVehicleFetch,
} from "../../../utils/utils";
import type { TradeInAemStateType } from "../../../redux/store";
import { type TradeInAemLabelType, getTradeInAemLabel } from "../../../utils/labels";
import { formatDate, formatNumber } from "../../../../common-deprecated/Globalize";
import type { DealerFinderSettingsType } from "../../../../common-deprecated/components/AemDealerFinder";

// ----------------------------------------------------------------------
// Types
// ----------------------------------------------------------------------
export type TradeInAemVehicleTypeFi = {
    bodyType: string;
    engineKW: number;
    enginePS: number;
    engineSize: number;
    fuelType: string;
    gearType: string;
    makeDescription: string;
    modelDescription: string;
    numberDoors: number;
    numberGears: number;
    periodOfBuildDetails: { poBFromMonth: number; poBFromYear: number; poBUntilMonth: number; poBUntilYear: number };
    priceBrutto: number;
    priceNetto: number;
    typeDescription: string;
    typeETGCode: string;
    vehType: string;
    vin: string;
    plate: string;
    registrationDate: string;
    trimLine: string;
    driveType: string;

    // Vehicle detail fields.
    mileage?: number;

    // Extra info property - used to save some data which will be used for the PDF later
    tmeExtraInfo?: TradeInAemVehicleExtraInfoType;
};

export type TradeInAemFiLicenseApiResponseType = {
    plate: string;
    vin: string;
    plateData: {
        ccm: number;
        co2Emissions: number;
        country: string;
        hp: number;
        watt: number;
        plate: string;
        registrationDate: string;
        vin: string;
    };
    specification: {
        vehicles: Omit<TradeInAemVehicleTypeFi, "plate" | "vin" | "registrationDate" | "tmeExtraInfo">[];
    };
};

export type TradeInAemFiValueApiResponseType = TradeInAemVehicleTypeFi & {
    quote?: {
        regionalAskingPrice: number;
        regionalSalesPrice: number;
        date: string;
        status: string;
        raw: unknown; // This data doesnt seem relevant to us, casting to unknown for now.
        value?: {
            min: number;
            rules: unknown[];
            detail: unknown[];
            expires: string;
        };
        error?: string;
        id: string;
    };
    context: unknown;
    firstRegistrationDate: string;
};

// Specific properties which are only shown for TAF in the vehicle extra details in the PDF
export type TradeInAemExtraVehicleDetailsPdfFiSpecificType = {
    engineSize: number;
    numberOfGears: number;
    priceNetto: number;
    priceBrutto: number;
    trimLine: string;
    driveType: string;
};

// Combination of base props + TAF specific props
export type TradeInAemFiExtraVehicleDetailsPdfType = TradeInAemExtraVehicleDetailsPdfBaseType &
    TradeInAemExtraVehicleDetailsPdfFiSpecificType;

// ----------------------------------------------------------------------
// CSL
// ----------------------------------------------------------------------

export class TradeInCslFi extends TradeInCsl {
    // ----------------------------------------------------------------------
    // Static properties
    // ----------------------------------------------------------------------
    enabledNavSteps = [
        TradeInAemNavStep.FindVehicle,
        TradeInAemNavStep.VehicleDetails,
        TradeInAemNavStep.ContactForm,
        TradeInAemNavStep.DealerFinder,
        TradeInAemNavStep.ModelSelect,
        TradeInAemNavStep.Summary,
    ];

    enableVin = false;

    enableLicensePlate = true;

    enableResolve = false;

    enablePreviouslySearchedVehicles = false;

    resolveKeyMap: Record<string, TradeInAemLabelType> = {};

    dealerFinderSettings: DealerFinderSettingsType = {};

    testPlates = [{ value: "BTV-925", description: "Multiple Results" }];

    vehicleValueSelector(state: TradeInAemStateType): TradeInAemVehicleValueType | null {
        const valuation = state.tradeInAemVehicleValue.valuation as TradeInAemFiValueApiResponseType | null;
        return valuation?.quote?.value
            ? {
                  value: valuation.quote.value.min,
                  validityDate: valuation.quote.value.expires,
                  quoteId: valuation.quote.id,
              }
            : null;
    }

    vehiclePlateOrVinSelector(state: TradeInAemStateType): string {
        const { searchType } = state.tradeInAemFindVehicle;
        const vehicle = state.tradeInAemVehicle.vehicle as TradeInAemVehicleTypeFi;
        return searchType === TradeInAemSearchType.LicensePlate ? vehicle.plate : vehicle.vin;
    }

    vehicleInfoBlockSelector(state: TradeInAemStateType): { label: string; value: string }[] {
        const vehicle = state.tradeInAemVehicle.vehicle as TradeInAemVehicleTypeFi;
        if (!vehicle) return [];

        const { formItems } = state.tradeInAemVehicleDetails;
        // The extra form items will be parsed and shown
        const { mileage, ...extraFormItems } = formItems;
        const mileageValue = formatNumber(Number(mileage.value as string), state.commonSettings.culture.name);
        const mileageLabel = getTradeInAemLabel(state, "tradeInAemMileageFormat").replace("{value}", mileageValue);

        return [
            { label: getTradeInAemLabel(state, "tradeInAemVehicleDetailsLicensePlate"), value: vehicle.plate },
            { label: mileage.label, value: mileageLabel },
            ...getParsedVehicleDetails(extraFormItems),
        ];
    }

    vehicleQuoteExpirySelector(state: TradeInAemStateType): string {
        const valuation = state.tradeInAemVehicleValue.valuation as TradeInAemFiValueApiResponseType | null;
        if (!valuation?.quote?.value) return "";

        const date = formatDate(
            new Date(valuation.quote.value.expires),
            "d MMMM yyyy",
            state.commonSettings.culture.name,
        );
        return getTradeInAemLabel(state, "tradeInAemVehicleValueValidity").replace("{date}", date);
    }

    // ----------------------------------------------------------------------
    // Non-selector functions
    // ----------------------------------------------------------------------
    getVehicleName(vehicle: TradeInAemVehicleType): TradeInAemVehicleNameType {
        const typedVehicle = vehicle as TradeInAemVehicleTypeFi;
        return {
            name: `${typedVehicle.makeDescription} ${typedVehicle.modelDescription} (${typedVehicle.periodOfBuildDetails.poBFromYear})`,
            description: typedVehicle.typeDescription,
        };
    }

    getVehicleKey(vehicle: TradeInAemVehicleType): string {
        const typedVehicle = vehicle as TradeInAemVehicleTypeFi;
        return `${typedVehicle.makeDescription}-${typedVehicle.modelDescription}-${typedVehicle.typeDescription}-${
            typedVehicle.typeETGCode
        }`;
    }

    async fetchVehicleResults(value: string, type: TradeInAemSearchType): Promise<void> {
        const { dispatch } = this.getStore();
        const { commonSettings, tradeInAemSettings } = this.getStore().getState();

        const isValidVehicle = (vehicle: TradeInAemVehicleTypeFi): boolean => {
            return !!vehicle.makeDescription && !!vehicle.modelDescription;
        };

        const fetchFn = async (): Promise<TradeInAemFetchVehicleResultsReturnType> => {
            const headers = this.getTradeInApiHeaders("GET");
            const response = await fetchFindVehicle(value, type, commonSettings, tradeInAemSettings, headers);
            let searchResults: TradeInAemVehicleTypeFi[];
            const jsonResponse = (await response.json()) as unknown as TradeInAemFiLicenseApiResponseType;

            if (response.status === 200) {
                searchResults = jsonResponse.specification.vehicles.map((vehicle) => {
                    return {
                        ...vehicle,
                        plate: jsonResponse.plate,
                        vin: jsonResponse.plateData.vin,
                        registrationDate: jsonResponse.plateData.registrationDate,
                    };
                });

                // Filter out all results without manufacturer and model as these are not valid vehicles
                return { results: searchResults.filter((result) => isValidVehicle(result)) };
            } else {
                return { results: [] };
            }
        };

        await handleVehicleFetch(dispatch, fetchFn);
    }

    async fetchVehicleQuote(): Promise<void> {
        const { dispatch, getState } = this.getStore();
        const { tradeInAemVehicle, commonSettings, tradeInAemSettings } = getState();
        const { vehicle } = tradeInAemVehicle;

        const fetchFn = async (): TradeInAemFetchQuoteFunctionReturnType => {
            const headers = this.getTradeInApiHeaders("POST");
            const payload: TradeInAemVehicleType & { mileage: number } = {
                ...vehicle,
                mileage: Number(vehicle?.mileage),
            };

            const response = await fetchVehicleValue(commonSettings, tradeInAemSettings, payload, headers);
            const jsonResponse = await response.json();
            if (response.status === 200) {
                const result = jsonResponse as TradeInAemFiValueApiResponseType;
                // The result is only valid when there is a quote
                if (result.quote?.value) return { valuation: result, latestResponse: jsonResponse };
            }
            return { valuation: null, latestResponse: jsonResponse };
        };
        await handleQuoteFetch(dispatch, fetchFn);
    }

    getEHubVehicleData(): TradeInAemEHubPayload["vehicle"] {
        const currentState = this.getStore().getState();
        const vehicleName = this.vehicleNameSelector(currentState);
        const { tradeInAemVehicle, tradeInAemVehicleDetails, commonSettings } = currentState;
        const { tradeInAemSettings } = currentState;
        const vehicle = tradeInAemVehicle.vehicle as TradeInAemVehicleTypeFi;

        const quoteId = this.vehicleValueSelector(currentState)?.quoteId || null;
        const pdfLink =
            enablePdf(commonSettings, tradeInAemSettings) && quoteId
                ? getTradeInAemPdfUrl(commonSettings, quoteId)
                : null;

        return {
            quoteId,
            licensePlate: vehicle.plate,
            name: `${vehicleName.name} - ${vehicleName.description || ""}`,
            manufacturer: vehicle.makeDescription,
            model: vehicle.modelDescription,
            bodyType: vehicle.bodyType,
            mileage: (tradeInAemVehicleDetails.formItems?.mileage?.value as string) || null,
            doors: vehicle.numberDoors.toString(),
            fuelType: vehicle.fuelType,
            engine: null,
            transmission: vehicle.gearType,
            co2: null,
            modelYear: vehicle.periodOfBuildDetails.poBFromYear.toString(),
            vin: vehicle.vin,
            pdfLink,
            registrationDate: vehicle.registrationDate,
        };
    }

    getExtraVehicleDetailsPdf = (): TradeInAemFiExtraVehicleDetailsPdfType => {
        const currentState = this.getStore().getState();
        const { tradeInAemVehicle } = currentState;
        // The vehicle on the PDF is fetched via the quoteId, which is propagated from the value api.
        const vehicle = tradeInAemVehicle.vehicle as TradeInAemFiValueApiResponseType;

        const baseObject: TradeInAemExtraVehicleDetailsPdfBaseType = {
            manufacturer: vehicle.makeDescription,
            model: vehicle.modelDescription,
            grade: null,
            generation: null,
            engine: null,
            powerHp: null,
            powerPs: vehicle.enginePS.toString(),
            powerKw: vehicle.engineKW.toString(),
            body: vehicle.bodyType,
            doors: vehicle.numberDoors.toString(),
            fuel: vehicle.fuelType,
            transmission: vehicle.gearType,
            gears: vehicle.numberGears.toString(),
            colour: null,
            regDate: vehicle.firstRegistrationDate,
            vin: vehicle.vin,
            seats: null,
            wheeldrive: null,
            cc: null,
            year: null,
        };

        // TAF specific
        const csObject: TradeInAemExtraVehicleDetailsPdfFiSpecificType = {
            engineSize: vehicle.engineSize,
            numberOfGears: vehicle.numberGears,
            priceBrutto: vehicle.priceBrutto,
            priceNetto: vehicle.priceNetto,
            trimLine: vehicle.trimLine,
            driveType: vehicle.driveType,
        };

        return { ...baseObject, ...csObject };
    };

    getTradeInApiHeaders(method: "GET" | "POST"): HeadersInit | undefined {
        // This header is needed for the v2 variant of the Volta api's for TAF.
        return { ...super.getTradeInApiHeaders(method), "X-TME-VERSION": "2" };
    }

    prefillFormsWithTestData(): void {
        super.prefillFormsWithTestData();
        // Prefill custom vehicle details values
        this.setFormItemValue("mileage", "100000");
        this.setFormItemValue("VehicleCondition", "Erinomainen");
    }
}
